import React, { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import './AdminFooterLinks.css';

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

const LOCALES = [
  { code: 'lt', label: 'Lietuvių' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

const DEFAULT_GROUPS = [
  {
    id: generateId(),
    title_lt: 'Parduotuvė', title_en: 'Shop', title_de: 'Shop',
    links: [
      { id: generateId(), label_lt: 'Produktai', label_en: 'Products', label_de: 'Produkte', type: 'url', pageSlug: '', url: '/products' },
      { id: generateId(), label_lt: 'Rekomenduojami', label_en: 'Featured', label_de: 'Empfohlen', type: 'url', pageSlug: '', url: '/products?featured=1' },
      { id: generateId(), label_lt: 'Kategorijos', label_en: 'Categories', label_de: 'Kategorien', type: 'url', pageSlug: '', url: '/categories' },
      { id: generateId(), label_lt: 'Krepšelis', label_en: 'Cart', label_de: 'Warenkorb', type: 'url', pageSlug: '', url: '/cart' },
    ],
  },
  {
    id: generateId(),
    title_lt: 'Informacija', title_en: 'Information', title_de: 'Information',
    links: [
      { id: generateId(), label_lt: 'Apie mus', label_en: 'About us', label_de: 'Über uns', type: 'url', pageSlug: '', url: '/about' },
      { id: generateId(), label_lt: 'Kontaktai', label_en: 'Contact', label_de: 'Kontakt', type: 'url', pageSlug: '', url: '/contact' },
      { id: generateId(), label_lt: 'Pristatymas', label_en: 'Shipping', label_de: 'Versand', type: 'url', pageSlug: '', url: '/shipping' },
      { id: generateId(), label_lt: 'Grąžinimas', label_en: 'Returns', label_de: 'Rückgabe', type: 'url', pageSlug: '', url: '/returns' },
    ],
  },
  {
    id: generateId(),
    title_lt: 'Paskyra', title_en: 'Account', title_de: 'Konto',
    links: [
      { id: generateId(), label_lt: 'Mano paskyra', label_en: 'My account', label_de: 'Mein Konto', type: 'url', pageSlug: '', url: '/my-account' },
      { id: generateId(), label_lt: 'Užsakymai', label_en: 'Orders', label_de: 'Bestellungen', type: 'url', pageSlug: '', url: '/orders' },
      { id: generateId(), label_lt: 'Mėgstamos prekės', label_en: 'Favorites', label_de: 'Favoriten', type: 'url', pageSlug: '', url: '/my-favorites' },
      { id: generateId(), label_lt: 'DUK', label_en: 'FAQ', label_de: 'FAQ', type: 'url', pageSlug: '', url: '/faq' },
    ],
  },
];

const DEFAULT_LEGAL_LINKS = [
  { id: generateId(), label_lt: 'Privatumo politika', label_en: 'Privacy policy', label_de: 'Datenschutz', type: 'url', pageSlug: '', url: '/privacy' },
  { id: generateId(), label_lt: 'Naudojimo sąlygos', label_en: 'Terms of use', label_de: 'Nutzungsbedingungen', type: 'url', pageSlug: '', url: '/terms' },
  { id: generateId(), label_lt: 'Slapukų politika', label_en: 'Cookie policy', label_de: 'Cookie-Richtlinie', type: 'url', pageSlug: '', url: '/cookies' },
];

function normalizeGroup(g) {
  const title = g.title ?? g.title_lt ?? '';
  const links = (g.links || []).map((l) => {
    const label = l.label ?? l.label_lt ?? '';
    return { ...l, label_lt: l.label_lt ?? label, label_en: l.label_en ?? '', label_de: l.label_de ?? '' };
  });
  return { ...g, title_lt: g.title_lt ?? title, title_en: g.title_en ?? '', title_de: g.title_de ?? '', links };
}

function normalizeLegalLink(l) {
  const label = l.label ?? l.label_lt ?? '';
  return { ...l, label_lt: l.label_lt ?? label, label_en: l.label_en ?? '', label_de: l.label_de ?? '' };
}

function AdminFooterLinks() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [groups, setGroups] = useState([]);
  const [legalLinks, setLegalLinks] = useState([]);
  const [activeLang, setActiveLang] = useState('lt');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-footer-links'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/footer-links');
      return res.data;
    },
  });

  const { data: pages = [] } = useQuery({
    queryKey: ['admin-pages'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/pages');
      return res.data;
    },
  });

  useEffect(() => {
    if (data?.content) {
      setGroups((Array.isArray(data.content.groups) ? data.content.groups : []).map(normalizeGroup));
      const savedLegal = data.content.legalLinks;
      if (Array.isArray(savedLegal) && savedLegal.length > 0) {
        setLegalLinks(savedLegal.map(normalizeLegalLink));
      } else {
        setLegalLinks(JSON.parse(JSON.stringify(DEFAULT_LEGAL_LINKS)));
      }
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      await apiClient.put('/admin/footer-links', { content: payload });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-footer-links']);
      queryClient.invalidateQueries(['footer-links-public']);
      notification.success('Footerio nuorodos išsaugotos');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida išsaugant');
    },
  });

  const addGroup = useCallback(() => {
    setGroups((prev) => [
      ...prev,
      { id: generateId(), title_lt: 'Nauja grupė', title_en: '', title_de: '', links: [] },
    ]);
  }, []);

  const updateGroup = useCallback((groupId, patch) => {
    setGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, ...patch } : g))
    );
  }, []);

  const removeGroup = useCallback((groupId) => {
    setGroups((prev) => prev.filter((g) => g.id !== groupId));
  }, []);

  const moveGroup = useCallback((fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= groups.length) return;
    setGroups((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return next;
    });
  }, [groups.length]);

  const addLink = useCallback((groupId) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? {
              ...g,
              links: [
                ...g.links,
                { id: generateId(), label_lt: '', label_en: '', label_de: '', type: 'url', pageSlug: '', url: '' },
              ],
            }
          : g
      )
    );
  }, []);

  const updateLink = useCallback((groupId, linkId, patch) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? {
              ...g,
              links: g.links.map((l) => (l.id === linkId ? { ...l, ...patch } : l)),
            }
          : g
      )
    );
  }, []);

  const removeLink = useCallback((groupId, linkId) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId ? { ...g, links: g.links.filter((l) => l.id !== linkId) } : g
      )
    );
  }, []);

  const moveLink = useCallback((groupId, fromIndex, toIndex) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        const links = [...g.links];
        if (toIndex < 0 || toIndex >= links.length) return g;
        const [removed] = links.splice(fromIndex, 1);
        links.splice(toIndex, 0, removed);
        return { ...g, links };
      })
    );
  }, []);

  const loadDefault = useCallback(() => {
    setGroups(JSON.parse(JSON.stringify(DEFAULT_GROUPS)));
    setLegalLinks(JSON.parse(JSON.stringify(DEFAULT_LEGAL_LINKS)));
    notification.success('Numatytos nuorodos įkeltos. Išsaugokite, jei norite jas naudoti.');
  }, [notification]);

  const addLegalLink = useCallback(() => {
    setLegalLinks((prev) => [
      ...prev,
      { id: generateId(), label_lt: '', label_en: '', label_de: '', type: 'url', pageSlug: '', url: '' },
    ]);
  }, []);

  const updateLegalLink = useCallback((linkId, patch) => {
    setLegalLinks((prev) =>
      prev.map((l) => (l.id === linkId ? { ...l, ...patch } : l))
    );
  }, []);

  const removeLegalLink = useCallback((linkId) => {
    setLegalLinks((prev) => prev.filter((l) => l.id !== linkId));
  }, []);

  const moveLegalLink = useCallback((fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= legalLinks.length) return;
    setLegalLinks((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return next;
    });
  }, [legalLinks.length]);

  const handleSave = useCallback(() => {
    saveMutation.mutate({ groups, legalLinks });
  }, [groups, legalLinks, saveMutation]);

  if (isLoading) {
    return (
      <div className="admin-footer-links-page container">
        <div className="admin-footer-links-loading">Kraunama...</div>
      </div>
    );
  }

  return (
    <div className="admin-footer-links-page container">
      <div className="footer-links-header">
        <h1>Footerio nuorodos</h1>
        <div className="footer-links-actions">
          {groups.length === 0 ? (
            <button type="button" className="btn btn-secondary" onClick={loadDefault}>
              Įkelti numatytą struktūrą
            </button>
          ) : (
            <button type="button" className="btn btn-secondary" onClick={addGroup}>
              + Pridėti grupę
            </button>
          )}
          <Link to="/" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
            Peržiūrėti puslapį
          </Link>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saveMutation.isLoading}
          >
            {saveMutation.isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
          </button>
        </div>
      </div>

      <p className="footer-links-hint">
        Pridėkite grupes ir nuorodas. Užpildykite pavadinimus lietuvių, anglų ir vokiečių kalbomis. Eiliškumą keiskite rodyklėmis.
      </p>

      <div className="admin-lang-tabs">
        {LOCALES.map(({ code, label }) => (
          <button
            key={code}
            type="button"
            className={`admin-lang-tab ${activeLang === code ? 'active' : ''}`}
            onClick={() => setActiveLang(code)}
          >
            {label}
          </button>
        ))}
      </div>

      {groups.length === 0 ? (
        <div className="footer-links-empty-state">
          <p>Footerio nuorodų grupių nėra. Įkelkite numatytą struktūrą arba pridėkite pirmąją grupę.</p>
          <button type="button" className="btn btn-primary" onClick={loadDefault}>
            Įkelti numatytą struktūrą
          </button>
        </div>
      ) : (
        <>
          <div className="footer-links-groups">
            {groups.map((group, groupIndex) => (
              <div key={group.id} className="footer-links-group-card">
                <div className="footer-links-group-head">
                  <div className="footer-links-group-move">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      title="Perkelti aukštyn"
                      disabled={groupIndex === 0}
                      onClick={() => moveGroup(groupIndex, groupIndex - 1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      title="Perkelti žemyn"
                      disabled={groupIndex === groups.length - 1}
                      onClick={() => moveGroup(groupIndex, groupIndex + 1)}
                    >
                      ↓
                    </button>
                  </div>
                  <div className="footer-links-group-title-wrap">
                    <label htmlFor={`group-title-${group.id}`}>Grupės pavadinimas ({LOCALES.find((l) => l.code === activeLang)?.label})</label>
                    <input
                      id={`group-title-${group.id}`}
                      type="text"
                      value={group[`title_${activeLang}`] ?? ''}
                      onChange={(e) => updateGroup(group.id, { [`title_${activeLang}`]: e.target.value })}
                      placeholder={activeLang === 'lt' ? 'Pvz. Parduotuvė' : activeLang === 'en' ? 'e.g. Shop' : 'z.B. Shop'}
                    />
                  </div>
                  <div className="footer-links-group-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => addLink(group.id)}
                    >
                      + Nuoroda
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => removeGroup(group.id)}
                      title="Pašalinti grupę"
                    >
                      Pašalinti grupę
                    </button>
                  </div>
                </div>

                {group.links.length === 0 ? (
                  <p className="footer-links-empty-msg">Šioje grupėje nėra nuorodų. Pridėkite nuorodą mygtuku „+ Nuoroda“.</p>
                ) : (
                  <div className="footer-links-table-wrap">
                    <table className="footer-links-table">
                      <thead>
                        <tr>
                          <th className="col-move">Eilė</th>
                          <th className="col-label">Pavadinimas</th>
                          <th className="col-type">Tipas</th>
                          <th className="col-target">Puslapis / URL</th>
                          <th className="col-remove"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.links.map((link, linkIndex) => (
                          <tr key={link.id}>
                            <td className="col-move">
                              <button
                                type="button"
                                className="btn btn-secondary"
                                title="Aukštyn"
                                disabled={linkIndex === 0}
                                onClick={() => moveLink(group.id, linkIndex, linkIndex - 1)}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                title="Žemyn"
                                disabled={linkIndex === group.links.length - 1}
                                onClick={() => moveLink(group.id, linkIndex, linkIndex + 1)}
                              >
                                ↓
                              </button>
                            </td>
                            <td className="col-label">
                              <input
                                type="text"
                                value={link[`label_${activeLang}`] ?? ''}
                                onChange={(e) => updateLink(group.id, link.id, { [`label_${activeLang}`]: e.target.value })}
                                placeholder={activeLang === 'lt' ? 'Pavadinimas' : activeLang === 'en' ? 'Label' : 'Bezeichnung'}
                              />
                            </td>
                            <td className="col-type">
                              <select
                                value={link.type || 'url'}
                                onChange={(e) =>
                                  updateLink(group.id, link.id, {
                                    type: e.target.value,
                                    pageSlug: e.target.value === 'page' ? (pages[0]?.slug || '') : '',
                                    url: e.target.value === 'url' ? link.url || '' : '',
                                  })
                                }
                              >
                                <option value="url">Nuoroda (URL)</option>
                                <option value="page">Sistemos puslapis</option>
                              </select>
                            </td>
                            <td className="col-target">
                              {link.type === 'page' ? (
                                <select
                                  value={link.pageSlug || ''}
                                  onChange={(e) => updateLink(group.id, link.id, { pageSlug: e.target.value })}
                                >
                                  <option value="">— Pasirinkite puslapį —</option>
                                  {pages.map((p) => (
                                    <option key={p.id} value={p.slug}>
                                      {p.title_lt || p.slug}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  value={link.url || ''}
                                  onChange={(e) => updateLink(group.id, link.id, { url: e.target.value })}
                                  placeholder="/kelias arba https://..."
                                />
                              )}
                            </td>
                            <td className="col-remove">
                              <button
                                type="button"
                                className="btn-remove"
                                onClick={() => removeLink(group.id, link.id)}
                                title="Pašalinti nuorodą"
                                aria-label="Pašalinti"
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="footer-links-add-group-wrap">
            <button type="button" className="btn btn-secondary" onClick={addGroup}>
              + Pridėti grupę
            </button>
          </div>
        </>
      )}

      <div className="footer-links-legal-card">
            <h3 className="footer-links-legal-title">Copyright / teisės nuorodos (apatinė juosta)</h3>
            <p className="footer-links-legal-hint">
              Šios nuorodos rodomos footerio apačioje šalia copyright teksto (pvz. Privatumo politika, Naudojimo sąlygos).
            </p>
            {legalLinks.length === 0 ? (
              <p className="footer-links-empty-msg">Nuorodų nėra. Pridėkite nuorodą.</p>
            ) : (
              <div className="footer-links-table-wrap">
                <table className="footer-links-table">
                  <thead>
                    <tr>
                      <th className="col-move">Eilė</th>
                      <th className="col-label">Pavadinimas</th>
                      <th className="col-type">Tipas</th>
                      <th className="col-target">Puslapis / URL</th>
                      <th className="col-remove"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {legalLinks.map((link, linkIndex) => (
                      <tr key={link.id}>
                        <td className="col-move">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            title="Aukštyn"
                            disabled={linkIndex === 0}
                            onClick={() => moveLegalLink(linkIndex, linkIndex - 1)}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            title="Žemyn"
                            disabled={linkIndex === legalLinks.length - 1}
                            onClick={() => moveLegalLink(linkIndex, linkIndex + 1)}
                          >
                            ↓
                          </button>
                        </td>
                        <td className="col-label">
                          <input
                            type="text"
                            value={link[`label_${activeLang}`] ?? ''}
                            onChange={(e) => updateLegalLink(link.id, { [`label_${activeLang}`]: e.target.value })}
                            placeholder={activeLang === 'lt' ? 'Pavadinimas' : activeLang === 'en' ? 'Label' : 'Bezeichnung'}
                          />
                        </td>
                        <td className="col-type">
                          <select
                            value={link.type || 'url'}
                            onChange={(e) =>
                              updateLegalLink(link.id, {
                                type: e.target.value,
                                pageSlug: e.target.value === 'page' ? (pages[0]?.slug || '') : '',
                                url: e.target.value === 'url' ? link.url || '' : '',
                              })
                            }
                          >
                            <option value="url">Nuoroda (URL)</option>
                            <option value="page">Sistemos puslapis</option>
                          </select>
                        </td>
                        <td className="col-target">
                          {link.type === 'page' ? (
                            <select
                              value={link.pageSlug || ''}
                              onChange={(e) => updateLegalLink(link.id, { pageSlug: e.target.value })}
                            >
                              <option value="">— Pasirinkite puslapį —</option>
                              {pages.map((p) => (
                                <option key={p.id} value={p.slug}>
                                  {p.title_lt || p.slug}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={link.url || ''}
                              onChange={(e) => updateLegalLink(link.id, { url: e.target.value })}
                              placeholder="/kelias arba https://..."
                            />
                          )}
                        </td>
                        <td className="col-remove">
                          <button
                            type="button"
                            className="btn-remove"
                            onClick={() => removeLegalLink(link.id)}
                            title="Pašalinti nuorodą"
                            aria-label="Pašalinti"
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button type="button" className="btn btn-secondary footer-links-add-legal" onClick={addLegalLink}>
        + Pridėti nuorodą
      </button>
      </div>
    </div>
  );
}

export default AdminFooterLinks;
